import React, { useState, useEffect } from "react";
import {
  ListItem,
  ListItemColumn,
  SVGIcon,
  MenuList,
  MenuListItem,
  AnchorLink,
  HOCs,
} from "@pearsontechnology/aero-components";
import BaseMoreButton from "components/MoreButton";
import StyledLink from "components/StyledLink";
import ActivityLink from "../ActivityLink";
import { CSVFetchExportLink } from "../CSVFetchExportLink";
import appConfig from 'appConfig';
import messages from "../messages";
import { intl } from "containers/LanguageProvider/IntlGlobalProvider";
import { mediaImportNotes, BVMediaExportFilename, BVMediaScoredExportFilename, getSBMediaGridLineItem } from '../BronteViewer/mediaImportData';
import ActivityService from "services/ActivityService";
import { ACTIVITY_TYPE } from 'services/ActivityService/activityTypes';
import { LessonName, Menu, MenuListItemText } from "./styles";
import classes from "./style.module.scss";
import PublicationService from "services/PublicationService";
import { loadSession } from "data/CurrentUser/sessionManager";
import { decodeResponse } from "Workspace/Publications/components/PublicationListItemActions/utils/fetchSBMediaGridData";
import { dispatch } from 'pluginServices/store';
import { pushToast } from 'containers/ToastManager/actions';

const showToastError = (message) => { dispatch(pushToast({ message: intl.formatMessage(message) })); };

const { enhanceWithPopper } = HOCs;
const MoreButtonWithPopper = enhanceWithPopper(BaseMoreButton);

export const CohortDeploymentListItems = ({
  deploymentId,
  activityId,
  parsedConfig,
  cohortId,
  updateParentDeploymentState,
  onShowEnrollmentLinks,
  hasEnrollmentLinks,
  deploymentListLength,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [deploymentDetails, setDeploymentDetails] = useState({});

  useEffect(() => {
    // Only fetch deployment details if there is only one deployment in the list
    // This is because the deployment details is considered as On Demand courses
    if (deploymentListLength === 1) {
      PublicationService.getDeploymentDetails(cohortId, deploymentId).then((details) => {
        setDeploymentDetails(details);
      }).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching deployment details:', error);
      });
    }
  }, [cohortId, deploymentId, deploymentListLength]);

  const handleGetLessons = async (activityId, deploymentId) => {
    const env = appConfig.getEnv();
    const csvData = [...mediaImportNotes];
    const parentActivityPluginData = await ActivityService.getActivityPlugIn(activityId);
    let lessons = [];

    /**
     * If the activity plugin type is a lesson, then it will return the lesson itself.
     * Else if the activity plugin type is a course or a chapter, then it will fetch all the lessons under it and return all the lessons.
     */
    if (parentActivityPluginData.plugin.type === ACTIVITY_TYPE.LESSON) {
      const parentActivityData = await ActivityService.getActivity(activityId);
      lessons.push(parentActivityData);
    } else {
      lessons = await ActivityService.getChildActivitiesByType(
        activityId,
        ACTIVITY_TYPE.LESSON,
        1
      );
    }

    const excelLineItems = lessons.map((lesson) =>
      getSBMediaGridLineItem(lesson, deploymentId, cohortId, env)
    );

    csvData.push(...excelLineItems);
    return csvData;
  };

  const handleDownloadMediaGrid = async (deploymentId) => {
    try {

      const token = loadSession();
      const baseUrl = appConfig.getSBMediaGridURLForClasses();
      const url = `${baseUrl}${deploymentId}/cohort/${cohortId}`;

      //Fetch the SB Media Grid file
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 500) {
          return showToastError(messages.internalServerErrorMessage);
        }
        if (response.status === 404) {
          return showToastError(messages.sbMediaGridDownloadFail);
        }
        throw new Error(`Unexpected error: ${response.status}`);
      }

      return await decodeResponse(response);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading media import file:', error);
      return null;
    }
  };

  return (
    <ListItem key={`deployments-item-${deploymentId}`}>
      <ListItemColumn colWidth="50%">
        <SVGIcon
          primary
          icon="duotoneCourse"
          viewBox="0 0 24 24"
          style={{ flexShrink: 0 }}
        />
        <LessonName>{parsedConfig.title}</LessonName>
      </ListItemColumn>
      <ListItemColumn colWidth="170px" colWidthMobile="60%" grow="0">
        <StyledLink aria-label="gradesProgress" to={`/classes/${cohortId}/grades/${deploymentId}/grades`}>
          Grades &amp; Progress
        </StyledLink>
      </ListItemColumn>
      <ListItemColumn colWidth="170px" colWidthMobile="60%" grow="0">
        <AnchorLink
          aria-label="learnspaceLink"
          target="_blank"
          icon="open_in_new"
          iconSize={18}
          href={`${appConfig.getLearnspaceURL()}/learn/${cohortId}/${deploymentId}`}
        >
          {intl.formatMessage(messages.learnspaceUrl)}
        </AnchorLink>
      </ListItemColumn>
      <ListItemColumn colWidth="34px" colWidthMobile="34px" grow="0">
        <MoreButtonWithPopper
          placement="bottom-end"
          hidepopperonclick
          preventOverFlow
        >
          <Menu>
            <MenuList>
              <MenuListItem
                name="updateDeployments"
                onClick={() =>
                  updateParentDeploymentState({
                    activityId,
                    cohortId,
                    deploymentId,
                    deploymentDetails,
                  })
                }
              >
                <MenuListItemText>
                  {intl.formatMessage(messages.updateDeployments)}
                </MenuListItemText>
              </MenuListItem>
              {hasEnrollmentLinks && (
                <MenuListItem
                  name="hasEnrollmentLinks"
                  onClick={() => onShowEnrollmentLinks(deploymentId)}
                >
                  <MenuListItemText>
                    {intl.formatMessage(messages.showEnrollmentLinks)}
                  </MenuListItemText>
                </MenuListItem>
              )}
              <ActivityLink activityId={activityId} />
              <MenuListItem className={classes.csvButton}>
                <MenuListItemText>
                  <CSVFetchExportLink
                    fileName={deploymentDetails?.isScored ? BVMediaScoredExportFilename : BVMediaExportFilename}
                    fetchCSVData={() =>
                      deploymentDetails?.isScored ? handleDownloadMediaGrid(deploymentId) : handleGetLessons(activityId, deploymentId)
                    }
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    activityId={activityId}
                    title={parsedConfig.title}
                  >
                    {intl.formatMessage(
                      messages.downloadSitebuilderMediaGrid
                    )}
                  </CSVFetchExportLink>
                </MenuListItemText>
              </MenuListItem>
            </MenuList>
          </Menu>
        </MoreButtonWithPopper>
      </ListItemColumn>
    </ListItem>
  );
};

export default CohortDeploymentListItems;

